import {
  blobFsSupported,
  makeSliceReader,
  mountLazyFile,
  type LazyBlobReader,
  type LazyFsNode,
} from './blobFs';
import type {
  AudioChunk,
  CoreAvSpec,
  CoreLoadOptions,
  DiscInfo,
  FrameCore,
  InputSnapshot,
  PixelFormat,
  SystemId,
  VideoFrame,
} from '../types';
import {
  isPsxMemcardConsistent,
  PSX_MEMCARD_BYTES,
} from '../../storage/psxMemcard';
import { appBuildId } from '../../appVersion';

/* -------------------------------------------------------------------------- */
/* Emscripten モジュールの最小型定義                                            */
/* -------------------------------------------------------------------------- */

type CFunc = (...args: unknown[]) => unknown;

interface EmscriptenModule {
  HEAPU8: Uint8Array;
  HEAPU16: Uint16Array;
  HEAPU32: Uint32Array;
  HEAP16: Int16Array;
  HEAP32: Int32Array;
  _malloc(size: number): number;
  _free(ptr: number): void;
  cwrap(name: string, ret: string | null, args: string[]): CFunc;
  addFunction(fn: (...args: never[]) => unknown, signature: string): number;
  removeFunction(ptr: number): void;
  FS: {
    writeFile(path: string, data: Uint8Array): void;
    readFile(path: string, opts?: { encoding?: string }): Uint8Array;
    mkdir(path: string): void;
    unlink(path: string): void;
    analyzePath(path: string): { exists: boolean; object?: LazyFsNode | undefined };
    /** 診断用。古い FS 実装には無いことがあるので存在確認して使う。 */
    readdir?(path: string): string[];
    stat?(path: string): { size: number };
    /** チャンク書き込み用。古い FS 実装には無いことがあるので存在確認して使う。 */
    open?(path: string, flags: string): unknown;
    write?(stream: unknown, buffer: Uint8Array, offset: number, length: number, position?: number): number;
    close?(stream: unknown): void;
  };
  /** 関数ポインタ呼び出し（ディスク交換用。コア再ビルド後に載る） */
  getWasmTableEntry?(ptr: number): (...args: number[]) => number;
  dynCall_i?(ptr: number): number;
  dynCall_ii?(ptr: number, a: number): number;
  dynCall_iiii?(ptr: number, a: number, b: number, c: number): number;
}

export type ModuleFactory = (overrides?: Record<string, unknown>) => Promise<EmscriptenModule>;

const DYNCALL_SHIMS = `
function dynCall_i(index) {
  var entry = (typeof getWasmTableEntry === 'function')
    ? getWasmTableEntry
    : (typeof Module !== 'undefined' && Module['getWasmTableEntry']);
  if (typeof entry === 'function') return entry(index)();
  return wasmTable.get(index)();
}
function dynCall_ii(index, a) {
  var entry = (typeof getWasmTableEntry === 'function')
    ? getWasmTableEntry
    : (typeof Module !== 'undefined' && Module['getWasmTableEntry']);
  if (typeof entry === 'function') return entry(index)(a);
  return wasmTable.get(index)(a);
}
function dynCall_iiii(index, a, b, c) {
  var entry = (typeof getWasmTableEntry === 'function')
    ? getWasmTableEntry
    : (typeof Module !== 'undefined' && Module['getWasmTableEntry']);
  if (typeof entry === 'function') return entry(index)(a, b, c);
  return wasmTable.get(index)(a, b, c);
}
`;

/**
 * DYNCALLS 無しでリンクされたコアは未定義の dynCall_i を参照し、
 * Safari で "Can't find variable: dynCall_i" になる。
 *
 * emscripten 3.1.x + EXPORT_ES6 のファクトリは無名
 * `async function(moduleArg = {}) { ... }`（createCore という名前は外の変数）。
 */
export function patchLibretroGlueSource(source: string): string {
  if (!/\bdynCall_i\b/.test(source)) return source;
  // 実体の function 宣言があるときだけスキップ（代入 Module['dynCall_i']=… は対象外）
  if (/function\s+dynCall_i\s*\(/.test(source)) return source;

  let out = source;

  // 1) 代入をインライン関数に置き換え（ファクトリ探索に依存しない）
  out = out.replace(
    /Module\[['"]dynCall_i['"]\]\s*=\s*dynCall_i\b/g,
    'Module["dynCall_i"]=function(index){var e=typeof getWasmTableEntry==="function"?getWasmTableEntry:Module["getWasmTableEntry"];return e(index)();}',
  );
  out = out.replace(
    /Module\[['"]dynCall_ii['"]\]\s*=\s*dynCall_ii\b/g,
    'Module["dynCall_ii"]=function(index,a){var e=typeof getWasmTableEntry==="function"?getWasmTableEntry:Module["getWasmTableEntry"];return e(index)(a);}',
  );
  out = out.replace(
    /Module\[['"]dynCall_iiii['"]\]\s*=\s*dynCall_iiii\b/g,
    'Module["dynCall_iiii"]=function(index,a,b,c){var e=typeof getWasmTableEntry==="function"?getWasmTableEntry:Module["getWasmTableEntry"];return e(index)(a,b,c);}',
  );

  // 2) ファクトリ先頭へ宣言を差し込み（invoke_i 内の裸の dynCall_i() 用）
  //    emcc 3.1.46: var createCore = (() => { return (async function(moduleArg = {}) { ...
  const factoryOpen =
    /((?:async\s+)?function\s*\(\s*moduleArg(?:\s*=\s*\{\s*\})?\s*\)\s*\{)|((?:async\s+)?function\s+createCore\s*\([^)]*\)\s*\{)/;
  if (factoryOpen.test(out)) {
    out = out.replace(factoryOpen, (m) => `${m}\n${DYNCALL_SHIMS}`);
  } else {
    console.warn('[libretro] dynCall shim: factory body not found; assignment rewrite only');
  }

  return out;
}

/** 既定の読み込み: 必要なら dynCall shim を差し込んでから ES モジュールとして取り込む。 */
async function defaultModuleLoader(coreName: string): Promise<ModuleFactory> {
  const url = `${CORE_BASE}/${coreName}_libretro.js`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`コアの取得に失敗しました (${res.status}): ${url}`);
  }
  const type = (res.headers.get('content-type') ?? '').toLowerCase();
  if (type.includes('html')) {
    throw new Error(`コア JS ではなく HTML が返されました（配置漏れの可能性）: ${url}`);
  }
  const raw = await res.text();
  if (/^\s*</.test(raw)) {
    throw new Error(`コア JS ではなく HTML が返されました（配置漏れの可能性）: ${url}`);
  }
  const patched = patchLibretroGlueSource(raw);
  if (patched === raw) {
    return (await import(/* @vite-ignore */ url)).default as ModuleFactory;
  }
  const blob = new Blob([patched], { type: 'text/javascript' });
  const blobUrl = URL.createObjectURL(blob);
  try {
    return (await import(/* @vite-ignore */ blobUrl)).default as ModuleFactory;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/* libretro 定数 */
const RETRO_PIXEL_FORMAT_0RGB1555 = 0;
const RETRO_PIXEL_FORMAT_XRGB8888 = 1;
const RETRO_PIXEL_FORMAT_RGB565 = 2;

const RETRO_ENVIRONMENT_SET_PIXEL_FORMAT = 10;
const RETRO_ENVIRONMENT_GET_SYSTEM_DIRECTORY = 9;
const RETRO_ENVIRONMENT_GET_SAVE_DIRECTORY = 31;
const RETRO_ENVIRONMENT_GET_CAN_DUPE = 3;
const RETRO_ENVIRONMENT_SET_VARIABLES = 16;
const RETRO_ENVIRONMENT_GET_VARIABLE = 15;
const RETRO_ENVIRONMENT_GET_VARIABLE_UPDATE = 17;
const RETRO_ENVIRONMENT_GET_LOG_INTERFACE = 27;
const RETRO_ENVIRONMENT_SET_DISK_CONTROL_INTERFACE = 13;
const RETRO_ENVIRONMENT_GET_DISK_CONTROL_INTERFACE_VERSION = 57;
const RETRO_ENVIRONMENT_SET_DISK_CONTROL_EXT_INTERFACE = 58;

const RETRO_MEMORY_SAVE_RAM = 0;

const RETRO_DEVICE_JOYPAD = 1;
const RETRO_DEVICE_ANALOG = 5;

/**
 * libretro のデバイス「サブクラス」ID。
 *
 *   #define RETRO_DEVICE_SUBCLASS(base, id) (((id + 1) << 8) | base)
 *
 * retro_set_controller_port_device へ渡すのはこの値であって、
 * 基底の RETRO_DEVICE_ANALOG (5) ではない。
 */
function retroDeviceSubclass(base: number, id: number): number {
  return ((id + 1) << 8) | base;
}

/**
 * PS1 コントローラ種別。
 *
 * pcsx_rearmed の retro_set_controller_port_device は
 * PSE_STANDARD / PSE_ANALOG / PSE_DUALSHOCK … といったサブクラス値しか
 * 見ておらず、**基底の RETRO_DEVICE_JOYPAD (1) / ANALOG (5) は switch の
 * どれにも当たらず default へ落ちて PSE_PAD_TYPE_NONE になる**。
 * つまりコントローラ未接続の扱いになり、入力が一切効かなくなる。
 *
 * DualShock はスティック付きタイトル向けだが、pad/memcard が同一 SIO を
 * 共有するため、古いタイトル（最終電車など）ではカード容量判定が
 * 食い違う報告がある。標準パッドならデジタル入力だけで SIO が短く、
 * メモリーカード書き込みとの相性が良い。スティックが必要なソフトは
 * 後日 DualShock 切替を足す。
 */
const RETRO_DEVICE_PSX_STANDARD = retroDeviceSubclass(RETRO_DEVICE_JOYPAD, 0);
const RETRO_DEVICE_INDEX_ANALOG_LEFT = 0;
const RETRO_DEVICE_INDEX_ANALOG_RIGHT = 1;

export interface LibretroCoreConfig {
  /** 例: 'fceumm' → /cores/fceumm_libretro.js を読む */
  coreName: string;
  systems: readonly SystemId[];
  /** ディスクイメージ等、FS 上にファイル実体が必要なら true */
  needsFilesystem?: boolean;
  /** BIOS の配置先ファイル名（PS1 = scph5501.bin など） */
  biosFilename?: string;
  /** core option の初期値 */
  variables?: Record<string, string>;
  /**
   * Emscripten モジュールの読み込み方法の差し替え。
   * 省略時は /cores/<coreName>_libretro.js を動的 import する。
   * テストで偽モジュールを注入するために開けてある。
   */
  loadModule?: () => Promise<ModuleFactory>;
}

const CORE_BASE = '/cores';
const SYSTEM_DIR = '/system';
const SAVE_DIR = '/saves';
const ROM_DIR = '/rom';

/** PS1 BIOS は 512 KiB。これ以外は pcsx が弾いて HLE に落ちる。 */
const PSX_BIOS_BYTES = 512 * 1024;
/** pcsx_rearmed が loadPSXBios で優先的に探すファイル名。 */
const PSX_BIOS_FILENAMES = [
  'scph5500.bin',
  'scph5501.bin',
  'scph5502.bin',
  'scph1001.bin',
  'scph7001.bin',
  'scph101.bin',
  'psxonpsp660.bin',
] as const;

/** 失敗時に画面へ出すコアログの行数。多すぎると読めない。 */
const CORE_LOG_LINES = 40;

/** 動いているビルドの識別子（CI では commit SHA が入る）。 */
function buildId(): string {
  return appBuildId();
}

/**
 * libretro C API を直接叩く汎用 WASM コアアダプタ。
 *
 * 想定するビルド:
 *   emcc ... -s MODULARIZE=1 -s EXPORT_ES6=1 -s ALLOW_TABLE_GROWTH=1 \
 *            -s EXPORTED_RUNTIME_METHODS='["cwrap","addFunction","FS"]' \
 *            -s EXPORTED_FUNCTIONS='["_malloc","_free","_retro_init", ...]'
 *
 * コアバイナリは配布ライセンスの都合でリポジトリに含めていない。
 * `docs/CORES.md` の手順で /public/cores に配置すると自動的に有効化される。
 */
export class LibretroWasmCore implements FrameCore {
  readonly id: string;
  readonly systems: readonly SystemId[];

  private cfg: LibretroCoreConfig;
  private mod: EmscriptenModule | null = null;
  private fnPtrs: number[] = [];
  /**
   * retro_init 済みか。
   *
   * pcsx_rearmed は retro_init 内の loadPSXBios() でだけ /system を走査する。
   * BIOS を load() 側で書いてから init すると HLE に落ちるため、
   * モジュール準備と retro_init を分け、BIOS 配置後に init する。
   */
  private retroInited = false;
  /** 遅延読み込み中のファイル（キャッシュ状況の確認用） */
  private readonly lazyFiles = new Map<string, LazyBlobReader>();
  /**
   * コアの標準出力／標準エラーの直近ログ。
   *
   * libretro のログコールバックは未実装で、コアは printf にフォールバックする。
   * その出力は端末のコンソールにしか出ないため、iPhone では事実上読めない。
   * 起動に失敗したときだけ画面へ出せるよう、ここに溜めておく。
   */
  private readonly coreLog: string[] = [];
  /** 直近の ROM 配置方法。失敗時の切り分けに使う。 */
  private placement: 'lazy' | 'chunked' | 'memory' | 'none' = 'none';

  // libretro エントリポイント
  private retro!: {
    init: CFunc;
    deinit: CFunc;
    loadGame: CFunc;
    unloadGame: CFunc;
    cheatReset: CFunc;
    cheatSet: CFunc;
    run: CFunc;
    reset: CFunc;
    serializeSize: CFunc;
    serialize: CFunc;
    unserialize: CFunc;
    getMemoryData: CFunc;
    getMemorySize: CFunc;
    getSystemAvInfo: CFunc;
    setControllerPortDevice: CFunc;
  };

  private pixelFormat: PixelFormat = 'RGB565';
  private avSpec: CoreAvSpec = {
    width: 256,
    height: 240,
    fps: 60,
    sampleRate: 44100,
    pixelFormat: 'RGB565',
  };

  private lastFrame: VideoFrame = {
    data: new Uint16Array(0),
    width: 0,
    height: 0,
    pitch: 0,
    format: 'RGB565',
  };
  private videoScratch: Uint16Array | Uint32Array = new Uint16Array(0);

  private audioAccum: Int16Array = new Int16Array(65536);
  private audioLen = 0;

  private input: InputSnapshot = {
    buttons: new Uint16Array(4),
    analog: { leftX: 0, leftY: 0, rightX: 0, rightY: 0 },
  };

  private variables: Record<string, string>;
  private variablesDirty = true;
  private strCache = new Map<string, number>();
  /** 起動中の機種。PS1 の共有カード保護に使う。 */
  private loadedSystem: SystemId | null = null;
  /**
   * PS1: ステート読込で上書きされないよう、確定済みのメモリーカード画像を保持する。
   * 巻き戻し／ステートロードのたびに差し戻す。
   */
  private pinnedSaveRam: Uint8Array | null = null;
  /**
   * PS1 shared モード: 仮想 FS 上のカードパス。
   * load_game 前に書いておくと SysReset / BIOS 起動時からカードが見える。
   */
  private psxMemcardPath: string | null = null;
  /**
   * PS1 shared モード: ヒープ上の Mcd1Data。
   * SAVE_RAM は libretro モードでしか公開されないので、署名で探す。
   */
  private psxMcdPtr: number | null = null;
  /**
   * libretro Disk Control（pcsx が retro_init 時に渡す）。
   * マルチディスク切替に使う。
   */
  private diskControl: {
    setEjectState: number;
    getEjectState: number;
    getImageIndex: number;
    setImageIndex: number;
    getNumImages: number;
    getImageLabel: number;
  } | null = null;

  constructor(cfg: LibretroCoreConfig) {
    this.cfg = cfg;
    this.id = `libretro:${cfg.coreName}`;
    this.systems = cfg.systems;
    this.variables = { ...(cfg.variables ?? {}) };
  }

  /**
   * コアファイルが配置済みかを事前確認する（レジストリの可用性判定用）。
   *
   * ステータスコードだけを見てはいけない。SPA フォールバックを持つ配信環境では
   * 存在しない /cores/xxx.js に対しても index.html が 200 で返るため、
   * 「コアがある」と誤判定してロード時に落ちる。Content-Type まで検証する。
   */
  static async probe(coreName: string): Promise<boolean> {
    const url = `${CORE_BASE}/${coreName}_libretro.js`;
    try {
      // HEAD が空 Content-Type / 405 になる配信もあるので、必要なら GET に落とす。
      let res = await fetch(url, { method: 'HEAD' });
      if (!res.ok || !(res.headers.get('content-type') ?? '')) {
        res = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-63' },
        });
      }
      if (!res.ok && res.status !== 206) return false;

      const type = (res.headers.get('content-type') ?? '').toLowerCase();
      // HTML が返ってきたら SPA フォールバック。JS で無いものはコアではない。
      if (type.includes('html')) return false;
      if (type.includes('javascript') || type.includes('ecmascript') || type.includes('wasm')) {
        return true;
      }
      // Content-Type が曖昧なときは先頭バイトで JS/ESM っぽいか見る
      if (res.body) {
        const text = await res.clone().text();
        return /export\s+(default|\{)|createCore|function\s*\(/.test(text);
      }
      return type === '';
    } catch {
      return false;
    }
  }

  async init(): Promise<void> {
    if (this.mod) return;

    const factory = this.cfg.loadModule
      ? await this.cfg.loadModule()
      : await defaultModuleLoader(this.cfg.coreName);

    this.mod = await factory({
      locateFile: (path: string) => `${CORE_BASE}/${path}`,
      print: (msg: string) => this.log(msg, false),
      printErr: (msg: string) => this.log(msg, true),
    });

    const m = this.mod;
    for (const dir of [SYSTEM_DIR, SAVE_DIR, ROM_DIR]) {
      if (!m.FS.analyzePath(dir).exists) m.FS.mkdir(dir);
    }

    this.retro = {
      init: m.cwrap('retro_init', null, []),
      deinit: m.cwrap('retro_deinit', null, []),
      loadGame: m.cwrap('retro_load_game', 'number', ['number']),
      unloadGame: m.cwrap('retro_unload_game', null, []),
      cheatReset: m.cwrap('retro_cheat_reset', null, []),
      cheatSet: m.cwrap('retro_cheat_set', null, ['number', 'number', 'number']),
      run: m.cwrap('retro_run', null, []),
      reset: m.cwrap('retro_reset', null, []),
      serializeSize: m.cwrap('retro_serialize_size', 'number', []),
      serialize: m.cwrap('retro_serialize', 'number', ['number', 'number']),
      unserialize: m.cwrap('retro_unserialize', 'number', ['number', 'number']),
      getMemoryData: m.cwrap('retro_get_memory_data', 'number', ['number']),
      getMemorySize: m.cwrap('retro_get_memory_size', 'number', ['number']),
      getSystemAvInfo: m.cwrap('retro_get_system_av_info', null, ['number']),
      setControllerPortDevice: m.cwrap('retro_set_controller_port_device', null, ['number', 'number']),
    };

    this.registerCallbacks(m);
    // retro_init は load() 側で BIOS を置いてから呼ぶ（ensureRetroInit）。
  }

  /**
   * BIOS を仮想 FS へ書き、必要なら retro_init する。
   *
   * pcsx_rearmed の loadPSXBios() は retro_init 時に一度だけ走る。
   * ここでファイルが無いと bios_saved[] が空のまま、以降は常に HLE BIOS になり、
   * 最終電車など一部タイトルでメモリーカード容量判定が壊れる。
   */
  private placeBiosAndInit(opts: CoreLoadOptions): void {
    const m = this.mod;
    if (!m) throw new Error('init() before load()');

    if (opts.bios) {
      if (opts.system === 'psx' && opts.bios.byteLength !== PSX_BIOS_BYTES) {
        throw new Error(
          `PS1 BIOS は ${PSX_BIOS_BYTES} バイト（512 KiB）である必要があります（今は ${opts.bios.byteLength} バイト）。` +
            '設定画面から正しい scph5500/5501/5502.bin などを入れ直してください。',
        );
      }
      // pcsx は内容からリージョンを推定し Config.Bios[region] にファイル名を入れる。
      // 同一中身を代表名へ複製しておくと、US BIOS でも JP タイトル起動時に
      // 「空スロット → 別リージョンへフォールバック」が効く（HLE より優先）。
      const targets = new Set<string>();
      if (this.cfg.biosFilename) targets.add(this.cfg.biosFilename);
      if (opts.biosFilename && /\.bin$/i.test(opts.biosFilename)) {
        targets.add(opts.biosFilename);
      }
      if (opts.system === 'psx') {
        for (const name of PSX_BIOS_FILENAMES) targets.add(name);
      }
      for (const name of targets) {
        m.FS.writeFile(`${SYSTEM_DIR}/${name}`, opts.bios);
      }
    }

    this.ensureRetroInit();

    if (opts.system === 'psx' && opts.bios) {
      this.assertRealPsxBiosLoaded();
    }
  }

  private ensureRetroInit(): void {
    if (this.retroInited) return;
    this.retro.init();
    this.retroInited = true;
  }

  /**
   * 実 BIOS を渡したのにコアが HLE に落ちていないかをログで確認する。
   * loadPSXBios は SysPrintf で状態を出す（print / printErr 経由で coreLog へ入る）。
   */
  private assertRealPsxBiosLoaded(): void {
    const joined = this.coreLog.join('\n');
    if (/Using HLE BIOS/i.test(joined) || /BIOS set to 'hle'/i.test(joined)) {
      throw new Error(
        'PS1 BIOS を配置しましたがコアが HLE BIOS を使っています。' +
          'メモリーカードの容量判定が壊れることがあるので、512 KiB の正規 BIOS を設定から入れ直してください。',
      );
    }
    if (/No BIOS files found/i.test(joined) || /No PlayStation BIOS file found/i.test(joined)) {
      throw new Error(
        'PS1 BIOS ファイルをコアが見つけられませんでした。' +
          '設定画面の BIOS を確認し、ゲームを起動し直してください。',
      );
    }
  }

  /* ------------------------------------------------------------------ */
  /* libretro コールバック                                               */
  /* ------------------------------------------------------------------ */

  private registerCallbacks(m: EmscriptenModule): void {
    const setEnv = m.cwrap('retro_set_environment', null, ['number']);
    const setVideo = m.cwrap('retro_set_video_refresh', null, ['number']);
    const setAudioSample = m.cwrap('retro_set_audio_sample', null, ['number']);
    const setAudioBatch = m.cwrap('retro_set_audio_sample_batch', null, ['number']);
    const setInputPoll = m.cwrap('retro_set_input_poll', null, ['number']);
    const setInputState = m.cwrap('retro_set_input_state', null, ['number']);

    const add = (fn: (...a: never[]) => unknown, sig: string): number => {
      const ptr = m.addFunction(fn, sig);
      this.fnPtrs.push(ptr);
      return ptr;
    };

    setEnv(add(this.onEnvironment as never, 'iii'));
    setVideo(add(this.onVideoRefresh as never, 'viiii'));
    setAudioSample(add(this.onAudioSample as never, 'vii'));
    setAudioBatch(add(this.onAudioBatch as never, 'iii'));
    setInputPoll(add(() => undefined, 'v'));
    setInputState(add(this.onInputState as never, 'iiiii'));
  }

  /** JS 文字列を WASM ヒープに置き、ポインタを返す（同じ文字列は使い回す） */
  private cstring(s: string): number {
    const cached = this.strCache.get(s);
    if (cached !== undefined) return cached;
    const m = this.mod!;
    const bytes = new TextEncoder().encode(s + '\0');
    const ptr = m._malloc(bytes.length);
    m.HEAPU8.set(bytes, ptr);
    this.strCache.set(s, ptr);
    return ptr;
  }

  private readCString(ptr: number): string {
    if (!ptr) return '';
    const heap = this.mod!.HEAPU8;
    let end = ptr;
    while (heap[end] !== 0) end++;
    return new TextDecoder().decode(heap.subarray(ptr, end));
  }

  private onEnvironment = (cmd: number, data: number): number => {
    const m = this.mod;
    if (!m) return 0;

    switch (cmd) {
      case RETRO_ENVIRONMENT_SET_PIXEL_FORMAT: {
        const fmt = m.HEAP32[data >> 2] ?? RETRO_PIXEL_FORMAT_RGB565;
        if (fmt === RETRO_PIXEL_FORMAT_XRGB8888) this.pixelFormat = 'XRGB8888';
        else if (fmt === RETRO_PIXEL_FORMAT_RGB565) this.pixelFormat = 'RGB565';
        else if (fmt === RETRO_PIXEL_FORMAT_0RGB1555) {
          // 0RGB1555 は今どき使うコアがほぼ無いので未対応として弾く
          console.warn('[libretro] 0RGB1555 pixel format is not supported');
          return 0;
        }
        this.avSpec.pixelFormat = this.pixelFormat;
        return 1;
      }

      case RETRO_ENVIRONMENT_GET_CAN_DUPE:
        m.HEAPU8[data] = 1;
        return 1;

      case RETRO_ENVIRONMENT_GET_SYSTEM_DIRECTORY:
        m.HEAPU32[data >> 2] = this.cstring(SYSTEM_DIR);
        return 1;

      case RETRO_ENVIRONMENT_GET_SAVE_DIRECTORY:
        m.HEAPU32[data >> 2] = this.cstring(SAVE_DIR);
        return 1;

      case RETRO_ENVIRONMENT_SET_VARIABLES: {
        // struct retro_variable { const char *key; const char *value; } の配列
        let p = data;
        for (;;) {
          const keyPtr = m.HEAPU32[p >> 2] ?? 0;
          if (!keyPtr) break;
          const key = this.readCString(keyPtr);
          const desc = this.readCString(m.HEAPU32[(p + 4) >> 2] ?? 0);
          if (!(key in this.variables)) {
            // "説明; 既定値|別の値|..." の先頭を既定値として採用
            const opts = desc.split(';')[1]?.trim().split('|') ?? [];
            const first = opts[0];
            if (first) this.variables[key] = first;
          }
          p += 8;
        }
        return 1;
      }

      case RETRO_ENVIRONMENT_GET_VARIABLE: {
        const key = this.readCString(m.HEAPU32[data >> 2] ?? 0);
        const value = this.variables[key];
        m.HEAPU32[(data + 4) >> 2] = value ? this.cstring(value) : 0;
        return value ? 1 : 0;
      }

      case RETRO_ENVIRONMENT_GET_VARIABLE_UPDATE:
        m.HEAPU8[data] = this.variablesDirty ? 1 : 0;
        this.variablesDirty = false;
        return 1;

      case RETRO_ENVIRONMENT_GET_LOG_INTERFACE:
        // ログコールバックは未実装。0 を返せばコアは printf にフォールバックする。
        return 0;

      case RETRO_ENVIRONMENT_GET_DISK_CONTROL_INTERFACE_VERSION:
        // v1 = EXT インタフェース対応。pcsx はこれを見て EXT を渡す。
        m.HEAPU32[data >> 2] = 1;
        return 1;

      case RETRO_ENVIRONMENT_SET_DISK_CONTROL_INTERFACE:
        this.bindDiskControl(data, false);
        return 1;

      case RETRO_ENVIRONMENT_SET_DISK_CONTROL_EXT_INTERFACE:
        this.bindDiskControl(data, true);
        return 1;

      default:
        return 0;
    }
  };

  /**
   * コアから渡された Disk Control 関数ポインタを控える。
   * struct 先頭 7 スロットは v0/v1 共通。EXT の get_image_label は +10 番目。
   */
  private bindDiskControl(structPtr: number, ext: boolean): void {
    const m = this.mod;
    if (!m || !structPtr) return;
    const u32 = m.HEAPU32;
    const base = structPtr >> 2;
    this.diskControl = {
      setEjectState: u32[base] ?? 0,
      getEjectState: u32[base + 1] ?? 0,
      getImageIndex: u32[base + 2] ?? 0,
      setImageIndex: u32[base + 3] ?? 0,
      getNumImages: u32[base + 4] ?? 0,
      // ext: set_initial_image(+7), get_image_path(+8), get_image_label(+9)
      getImageLabel: ext ? (u32[base + 9] ?? 0) : 0,
    };
  }

  /** WASM 関数ポインタを呼ぶ。dynCall / getWasmTableEntry のどちらかが必要。 */
  private callWasm(sig: 'i' | 'ii' | 'iiii', fnPtr: number, ...args: number[]): number {
    if (!fnPtr) throw new Error('ディスク制御関数がありません');
    const m = this.mod;
    if (!m) throw new Error('コアが初期化されていません');

    if (typeof m.getWasmTableEntry === 'function') {
      const fn = m.getWasmTableEntry(fnPtr);
      return (fn(...args) | 0) as number;
    }
    if (sig === 'i' && typeof m.dynCall_i === 'function') return m.dynCall_i(fnPtr) | 0;
    if (sig === 'ii' && typeof m.dynCall_ii === 'function') {
      return m.dynCall_ii(fnPtr, args[0] ?? 0) | 0;
    }
    if (sig === 'iiii' && typeof m.dynCall_iiii === 'function') {
      return m.dynCall_iiii(fnPtr, args[0] ?? 0, args[1] ?? 0, args[2] ?? 0) | 0;
    }
    throw new Error(
      'このコアビルドはディスク交換に未対応です。アプリを最新版に更新してください。',
    );
  }

  getDiscInfo(): DiscInfo {
    const dc = this.diskControl;
    if (!dc?.getNumImages) {
      return { count: 1, index: 0, labels: ['Disc 1'] };
    }
    const count = Math.max(0, this.callWasm('i', dc.getNumImages));
    const index = dc.getImageIndex ? this.callWasm('i', dc.getImageIndex) : 0;
    const labels: string[] = [];
    for (let i = 0; i < count; i++) {
      let label = `Disc ${i + 1}`;
      if (dc.getImageLabel) {
        const buf = this.mod!._malloc(256);
        try {
          const ok = this.callWasm('iiii', dc.getImageLabel, i, buf, 255);
          if (ok) {
            const s = this.readCString(buf).trim();
            if (s) label = s;
          }
        } finally {
          this.mod!._free(buf);
        }
      }
      labels.push(label);
    }
    return { count, index, labels };
  }

  swapDisc(index: number): void {
    const dc = this.diskControl;
    if (!dc?.setEjectState || !dc.setImageIndex) {
      throw new Error('このコアはディスク交換に対応していません');
    }
    const info = this.getDiscInfo();
    if (index < 0 || index >= info.count) {
      throw new Error(`ディスク ${index + 1} はありません（全 ${info.count} 枚）`);
    }
    if (index === info.index) return;

    this.callWasm('ii', dc.setEjectState, 1);
    const ok = this.callWasm('ii', dc.setImageIndex, index);
    this.callWasm('ii', dc.setEjectState, 0);
    if (!ok) {
      throw new Error(`ディスク ${index + 1} への切り替えに失敗しました`);
    }
  }

  private onVideoRefresh = (dataPtr: number, width: number, height: number, pitch: number): void => {
    const m = this.mod;
    if (!m || !dataPtr || width === 0 || height === 0) return;

    // dupe フレーム (dataPtr === 0) は前フレームを維持する
    if (this.pixelFormat === 'XRGB8888') {
      const words = (pitch >> 2) * height;
      if (this.videoScratch.length < words || !(this.videoScratch instanceof Uint32Array)) {
        this.videoScratch = new Uint32Array(words);
      }
      (this.videoScratch as Uint32Array).set(m.HEAPU32.subarray(dataPtr >> 2, (dataPtr >> 2) + words));
    } else {
      const words = (pitch >> 1) * height;
      if (this.videoScratch.length < words || !(this.videoScratch instanceof Uint16Array)) {
        this.videoScratch = new Uint16Array(words);
      }
      (this.videoScratch as Uint16Array).set(m.HEAPU16.subarray(dataPtr >> 1, (dataPtr >> 1) + words));
    }

    this.lastFrame = {
      data: this.videoScratch,
      width,
      height,
      pitch,
      format: this.pixelFormat,
    };
  };

  private pushAudio(left: number, right: number): void {
    if (this.audioLen + 2 > this.audioAccum.length) return;
    this.audioAccum[this.audioLen++] = left;
    this.audioAccum[this.audioLen++] = right;
  }

  private onAudioSample = (left: number, right: number): void => {
    this.pushAudio(left, right);
  };

  private onAudioBatch = (dataPtr: number, frames: number): number => {
    const m = this.mod;
    if (!m) return frames;
    const src = m.HEAP16.subarray(dataPtr >> 1, (dataPtr >> 1) + frames * 2);
    const room = this.audioAccum.length - this.audioLen;
    const n = Math.min(src.length, room);
    this.audioAccum.set(src.subarray(0, n), this.audioLen);
    this.audioLen += n;
    return frames;
  };

  private onInputState = (port: number, device: number, index: number, id: number): number => {
    if (device === RETRO_DEVICE_JOYPAD) {
      const mask = this.input.buttons[port] ?? 0;
      return (mask >> id) & 1;
    }
    if (device === RETRO_DEVICE_ANALOG && port === 0) {
      const a = this.input.analog;
      const axis =
        index === RETRO_DEVICE_INDEX_ANALOG_LEFT
          ? id === 0
            ? a.leftX
            : a.leftY
          : index === RETRO_DEVICE_INDEX_ANALOG_RIGHT
            ? id === 0
              ? a.rightX
              : a.rightY
            : 0;
      return Math.round(Math.max(-1, Math.min(1, axis)) * 32767);
    }
    return 0;
  };

  /* ------------------------------------------------------------------ */
  /* ライフサイクル                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * ROM を読み込む。
   *
   * 遅延読み込みで失敗した場合は、コアを作り直してから
   * チャンク書き込みで一度だけやり直す。遅延読み込みは
   * MEMFS の内部構造に手を入れているぶん、コアやビルドの差で
   * 噛み合わないことがありうる。そのときに「起動できない」で
   * 終わらせず、遅いが確実な経路へ落ちられるようにしておく。
   */
  async load(opts: CoreLoadOptions): Promise<CoreAvSpec> {
    try {
      return await this.attemptLoad(opts, false);
    } catch (first) {
      if (this.placement !== 'lazy') throw first;

      console.warn('[libretro] 遅延読み込みで起動できず。チャンク書き込みで再試行します', first);
      const firstMessage = first instanceof Error ? first.message : String(first);

      opts.onProgress?.({ percent: 48, label: '遅延読み込みに失敗。再試行中…' });
      this.dispose();
      this.coreLog.length = 0;
      this.lazyFiles.clear();
      await this.init();
      opts.onProgress?.({ percent: 55, label: 'ディスクを再配置中' });

      try {
        return await this.attemptLoad(opts, true);
      } catch (second) {
        const secondMessage = second instanceof Error ? second.message : String(second);
        throw new Error(
          `${secondMessage}\n\n--- 遅延読み込みでの 1 回目 ---\n${firstMessage}`,
        );
      }
    }
  }

  private async attemptLoad(opts: CoreLoadOptions, forceEager: boolean): Promise<CoreAvSpec> {
    const m = this.mod;
    if (!m) throw new Error('init() before load()');
    const report = opts.onProgress;

    report?.({ percent: 2, label: opts.bios ? 'BIOS を配置中' : 'コアを初期化中' });
    // loadPSXBios は retro_init 時のみ。BIOS を先に書いてから init する。
    this.placeBiosAndInit(opts);

    // struct retro_game_info { const char *path; const void *data; size_t size; const char *meta; }
    const infoPtr = m._malloc(16);
    let romPtr = 0;
    let pathPtr = 0;

    if (this.cfg.needsFilesystem) {
      // CD イメージ等はメモリ渡しでは動かないので実ファイルとして置く。
      // CUE 内の FILE 名と一致させるため、basename はできるだけそのまま使う。
      const primary = fsSafeName(opts.romName);
      const romPath = `${ROM_DIR}/${primary}`;

      const parts: Array<{ path: string; data: Uint8Array | Blob; name: string }> = [
        { path: romPath, data: opts.rom, name: primary },
      ];
      if (opts.extraFiles) {
        for (const [name, data] of Object.entries(opts.extraFiles)) {
          const safe = fsSafeName(name);
          if (safe === primary) continue;
          parts.push({ path: `${ROM_DIR}/${safe}`, data, name: safe });
        }
      }

      const sizes = parts.map((p) => byteLengthOf(p.data));
      const totalBytes = Math.max(1, sizes.reduce((a, b) => a + b, 0));
      let placed = 0;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]!;
        const size = sizes[i]!;
        const base = placed;
        await this.placeFile(part.path, part.data, forceEager, (written) => {
          const overall = ((base + written) / totalBytes) * 92;
          report?.({
            percent: Math.min(92, Math.round(overall)),
            label: `ディスクを展開中 (${part.name})`,
          });
        });
        placed += size;
        // 1 件ごとにタスクを譲り、巨大ファイルの連続展開でフリーズさせない
        await new Promise((r) => setTimeout(r, 0));
      }

      report?.({ percent: 94, label: 'コアを起動中（応答まで時間がかかることがあります）' });
      // 進捗メッセージをメインへ届けてから同期の loadGame に入る
      await new Promise((r) => setTimeout(r, 0));
      pathPtr = this.cstring(romPath);
      m.HEAPU32[infoPtr >> 2] = pathPtr;
      m.HEAPU32[(infoPtr + 4) >> 2] = 0;
      m.HEAPU32[(infoPtr + 8) >> 2] = 0;
    } else {
      report?.({ percent: 40, label: 'ROM を展開中' });
      const romBytes = await toU8(opts.rom);
      report?.({ percent: 80, label: 'コアを起動中（応答まで時間がかかることがあります）' });
      await new Promise((r) => setTimeout(r, 0));
      romPtr = m._malloc(romBytes.length);
      m.HEAPU8.set(romBytes, romPtr);
      pathPtr = this.cstring(`${ROM_DIR}/${fsSafeName(opts.romName)}`);
      m.HEAPU32[infoPtr >> 2] = pathPtr;
      m.HEAPU32[(infoPtr + 4) >> 2] = romPtr;
      m.HEAPU32[(infoPtr + 8) >> 2] = romBytes.length;
    }
    m.HEAPU32[(infoPtr + 12) >> 2] = 0;

    // pcsx_rearmed は load_game 内の load_memcards() で GET_VARIABLE する。
    this.loadedSystem = opts.system;
    this.pinnedSaveRam = null;
    this.psxMemcardPath = null;
    this.psxMcdPtr = null;
    if (opts.system === 'psx') {
      // shared: load_memcards → LoadMcd が SysReset 前にカードを読む。
      // libretro + load 後 setSram だと BIOS/起動時は空の init_memcard のままになり、
      // 最終電車などが容量判定を誤ることがある。
      const card = opts.sram?.length
        ? opts.sram
        : new Uint8Array(PSX_MEMCARD_BYTES);
      const path = `${SAVE_DIR}/pcsx-card1.mcd`;
      m.FS.writeFile(path, card);
      this.psxMemcardPath = path;
      this.pinnedSaveRam = card.slice();
      this.variables['pcsx_rearmed_memcard1'] = 'shared';
      this.variables['pcsx_rearmed_memcard2'] = 'none';
      this.variablesDirty = true;
    }

    const ok = this.retro.loadGame(infoPtr) as number;
    m._free(infoPtr);
    if (romPtr) m._free(romPtr);
    if (!ok) {
      throw new Error(
        `${this.cfg.coreName}: retro_load_game failed\n\n${this.loadFailureDetail()}`,
      );
    }

    // PS1 は標準パッド（デジタル）を明示する。
    // DualShock は一部タイトルでメモリーカードの容量判定を誤らせる報告があり、
    // 基底 JOYPAD(1) だと未接続扱いになるのでサブクラス STANDARD を渡す。
    if (opts.system === 'psx') {
      this.retro.setControllerPortDevice(0, RETRO_DEVICE_PSX_STANDARD);
      // shared モードでは SAVE_RAM が公開されない。Mcd1Data を署名で拾う。
      if (this.pinnedSaveRam) {
        this.psxMcdPtr = this.findPsxMemcardPtr(this.pinnedSaveRam);
        if (this.psxMcdPtr == null) {
          console.warn(
            '[libretro] PS1 Mcd1Data をヒープから見つけられませんでした。FS 経由で永続化します。',
          );
        }
      }
    } else if (opts.sram) {
      const applied = this.setSram(opts.sram);
      if (!applied) {
        console.warn('[libretro] SRAM を SAVE_RAM に書けませんでした');
      }
    }

    report?.({ percent: 100, label: '準備完了' });
    return this.readAvInfo();
  }

  /**
   * shared モードで LoadMcd 済みの Mcd1Data をヒープから探す。
   * 先頭フレーム（128 B）が一致する位置を採用する（16 バイト境界まで見る）。
   */
  private findPsxMemcardPtr(card: Uint8Array): number | null {
    const m = this.mod;
    if (!m || card.length < 128) return null;
    const heap = m.HEAPU8;
    const limit = Math.max(0, heap.length - PSX_MEMCARD_BYTES);
    const sig = card.subarray(0, 128);
    // Emscripten malloc は 16 バイト境界。128 境界だけだと取りこぼす。
    for (let i = 0; i <= limit; i += 16) {
      if (heap[i] !== sig[0] || heap[i + 1] !== sig[1]) continue;
      let ok = true;
      for (let j = 2; j < 128; j++) {
        if (heap[i + j] !== sig[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return i;
    }
    return null;
  }

  /** コアの出力を控える。上限を設けて無制限に膨らませない。 */
  private log(msg: string, isError: boolean): void {
    const line = String(msg);
    if (isError) console.warn(`[${this.cfg.coreName}]`, line);
    else console.info(`[${this.cfg.coreName}]`, line);
    this.coreLog.push(line);
    if (this.coreLog.length > CORE_LOG_LINES) this.coreLog.shift();
  }

  /**
   * 起動に失敗した理由を、できるだけ具体的に組み立てる。
   *
   * 「retro_load_game failed」だけでは、BIOS が無いのか、CUE が参照する
   * BIN が見つからないのか、遅延読み込みが噛み合っていないのかが分からない。
   * 実機（iPhone）ではコンソールも見られないので、判断材料を画面へ出す。
   */
  private loadFailureDetail(): string {
    const parts: string[] = [];
    // どのビルドで起きたのかを最初に出す。
    // これが無いと、Service Worker が古い版を出しているだけなのか
    // 本当に直っていないのかを、貼られた文面から判断できない。
    parts.push(`ビルド: ${buildId()}`);
    parts.push(`配置方法: ${this.placement}`);

    const m = this.mod;
    if (m && typeof m.FS.readdir === 'function') {
      for (const dir of [ROM_DIR, SYSTEM_DIR]) {
        try {
          const names = m.FS.readdir(dir).filter((n) => n !== '.' && n !== '..');
          const listed = names.map((n) => {
            let size = '?';
            try {
              size = String(m.FS.stat?.(`${dir}/${n}`).size ?? '?');
            } catch {
              /* サイズが取れなくても名前だけ出す価値がある */
            }
            return `  ${n} (${size} bytes)`;
          });
          parts.push(`${dir}:\n${listed.length ? listed.join('\n') : '  (空)'}`);
        } catch {
          parts.push(`${dir}: 読めません`);
        }
      }
    }

    if (this.coreLog.length > 0) {
      parts.push(`コアの出力（末尾 ${this.coreLog.length} 行）:\n${this.coreLog.join('\n')}`);
    } else {
      parts.push('コアの出力: なし');
    }
    return parts.join('\n\n');
  }

  /**
   * ディスクイメージ等を仮想 FS へ置く。
   *
   * 大きいファイルを `FS.writeFile(await blob.arrayBuffer())` で置くと、
   * JS ヒープ側の展開と MEMFS 側のコピーでピークがサイズの 2 倍になる。
   * 600MB の PS1 イメージなら 1.2GB を要求することになり、
   * iOS Safari はそこへ届く前にタブごと落ちる。
   *
   * 対策を 2 段構えにしてある。
   *
   *   1. Worker 上（FileReaderSync が使える）なら**遅延読み込み**。
   *      実体を載せず、コアが要求したセクタだけ Blob から切り出す。
   *      常駐量がディスクサイズと無関係になる。
   *   2. それ以外はチャンク書き込み。MEMFS には全体が載るが、
   *      少なくとも 2 倍のピークは消える。
   */
  private async placeFile(
    path: string,
    data: Uint8Array | Blob,
    forceEager = false,
    onBytes?: (written: number) => void,
  ): Promise<void> {
    const m = this.mod!;

    if (data instanceof Uint8Array) {
      m.FS.writeFile(path, data);
      this.placement = 'memory';
      onBytes?.(data.byteLength);
      return;
    }

    // ArrayBufferView（一部環境の TextEncoder 結果など）も一括書き込み
    if (ArrayBuffer.isView(data)) {
      const view = data as ArrayBufferView;
      const copy = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
      m.FS.writeFile(path, copy);
      this.placement = 'memory';
      onBytes?.(copy.byteLength);
      return;
    }

    if (blobFsSupported() && !forceEager) {
      try {
        const reader = mountLazyFile(m.FS, path, data.size, makeSliceReader(data));
        this.lazyFiles.set(path, reader);
        this.placement = 'lazy';
        onBytes?.(data.size);
        return;
      } catch (e) {
        // ノードの内部構造は Emscripten のバージョンで変わりうる。
        // 失敗しても起動そのものは諦めず、チャンク書き込みへ落とす。
        console.warn('[libretro] 遅延読み込みに失敗。チャンク書き込みへ切り替えます', e);
      }
    }

    await this.writeChunked(path, data, onBytes);
    this.placement = 'chunked';
  }

  /** Blob を分割して MEMFS へ書き込む（全体を一度に展開しない）。 */
  private async writeChunked(
    path: string,
    blob: Blob,
    onBytes?: (written: number) => void,
  ): Promise<void> {
    const m = this.mod!;
    const CHUNK = 8 * 1024 * 1024;

    // FS.open/write/close が無い環境（テスト用のモック等）では従来どおり
    if (typeof m.FS.open !== 'function' || typeof m.FS.write !== 'function') {
      m.FS.writeFile(path, new Uint8Array(await blob.arrayBuffer()));
      onBytes?.(blob.size);
      return;
    }

    const stream = m.FS.open(path, 'w+');
    try {
      for (let pos = 0; pos < blob.size; pos += CHUNK) {
        const end = Math.min(pos + CHUNK, blob.size);
        const chunk = new Uint8Array(await blob.slice(pos, end).arrayBuffer());
        m.FS.write(stream, chunk, 0, chunk.length, pos);
        onBytes?.(end);
        // UI や入力イベントに隙間を空ける
        if (end < blob.size) await new Promise((r) => setTimeout(r, 0));
      }
    } finally {
      m.FS.close?.(stream);
    }
  }

  /** struct retro_system_av_info を読み出す */
  private readAvInfo(): CoreAvSpec {
    const m = this.mod!;
    // geometry(4*u32) + aspect(f32) + timing(f64 fps, f64 sample_rate)
    const ptr = m._malloc(64);
    this.retro.getSystemAvInfo(ptr);
    const u32 = m.HEAPU32;
    const f64 = new Float64Array(m.HEAPU8.buffer, ptr + 24, 2);

    const maxWidth = u32[(ptr + 8) >> 2] ?? 256;
    const maxHeight = u32[(ptr + 12) >> 2] ?? 240;
    const fps = f64[0] ?? 60;
    const sampleRate = f64[1] ?? 44100;
    m._free(ptr);

    // 1 フレーム分の音声が必ず収まるバッファを確保し直す
    const perFrame = Math.ceil(sampleRate / fps) * 2 + 64;
    if (this.audioAccum.length < perFrame * 2) {
      this.audioAccum = new Int16Array(perFrame * 2);
    }

    this.avSpec = {
      width: maxWidth,
      height: maxHeight,
      fps,
      sampleRate,
      pixelFormat: this.pixelFormat,
    };
    return this.avSpec;
  }

  runFrame(input: InputSnapshot): VideoFrame {
    this.input = input;
    this.audioLen = 0;
    this.retro.run();
    return this.lastFrame;
  }

  drainAudio(): AudioChunk | null {
    if (this.audioLen === 0) return null;
    // FrameDriver は同期的に消費するので subarray のままで安全
    return {
      samples: this.audioAccum.subarray(0, this.audioLen),
      sampleRate: this.avSpec.sampleRate,
    };
  }

  reset(): void {
    this.retro.reset();
  }

  saveState(): Uint8Array {
    const m = this.mod!;
    const size = this.retro.serializeSize() as number;
    if (size === 0) throw new Error(`${this.cfg.coreName} does not support save states`);
    const ptr = m._malloc(size);
    try {
      const ok = this.retro.serialize(ptr, size) as number;
      if (!ok) throw new Error('retro_serialize failed');
      return new Uint8Array(m.HEAPU8.subarray(ptr, ptr + size));
    } finally {
      m._free(ptr);
    }
  }

  loadState(state: Uint8Array): void {
    const m = this.mod!;
    const ptr = m._malloc(state.length);
    try {
      m.HEAPU8.set(state, ptr);
      const ok = this.retro.unserialize(ptr, state.length) as number;
      if (!ok) throw new Error('retro_unserialize failed (state/core mismatch?)');
    } finally {
      m._free(ptr);
    }
    // pcsx のステートにはメモリーカードが含まれる。共有カードをステートで潰さない。
    if (this.loadedSystem === 'psx' && this.pinnedSaveRam) {
      this.setSram(this.pinnedSaveRam);
    }
  }

  /**
   * チートを適用する。libretro のコアはコード文字列を自分で解析するので、
   * こちらは正規化した文字列をそのまま渡す。
   * 毎回 reset してから積み直すことで、削除・無効化も反映される。
   */
  setCheats(codes: string[]): void {
    if (!this.mod) return;
    try {
      this.retro.cheatReset();
      codes.forEach((code, i) => {
        this.retro.cheatSet(i, 1, this.cstring(code));
      });
    } catch (e) {
      console.warn('[libretro] cheats are not supported by this core', e);
    }
  }

  getSram(): Uint8Array | null {
    const m = this.mod!;

    // PS1 shared: ライブ Mcd1Data（見つかっていれば）→ なければ FS（SaveMcd が更新）
    if (this.loadedSystem === 'psx') {
      if (this.psxMcdPtr != null) {
        const copy = new Uint8Array(
          m.HEAPU8.subarray(this.psxMcdPtr, this.psxMcdPtr + PSX_MEMCARD_BYTES),
        );
        if (isPsxMemcardConsistent(copy)) this.pinnedSaveRam = copy.slice();
        // ファイルも追従させておく（pause flush 用）
        if (this.psxMemcardPath) {
          try {
            m.FS.writeFile(this.psxMemcardPath, copy);
          } catch {
            /* FS 失敗はライブ優先 */
          }
        }
        return copy;
      }
      if (this.psxMemcardPath) {
        try {
          const file = m.FS.readFile(this.psxMemcardPath);
          const copy = new Uint8Array(file);
          if (isPsxMemcardConsistent(copy)) this.pinnedSaveRam = copy.slice();
          return copy;
        } catch {
          return null;
        }
      }
    }

    const size = this.retro.getMemorySize(RETRO_MEMORY_SAVE_RAM) as number;
    if (!size) return null;
    const ptr = this.retro.getMemoryData(RETRO_MEMORY_SAVE_RAM) as number;
    if (!ptr) return null;
    const copy = new Uint8Array(m.HEAPU8.subarray(ptr, ptr + size));
    if (this.loadedSystem === 'psx' && isPsxMemcardConsistent(copy)) {
      this.pinnedSaveRam = copy.slice();
    }
    return copy;
  }

  /** PS1 カード修復後などに SAVE_RAM / Mcd1Data をライブへ戻す。 */
  setSram(data: Uint8Array): boolean {
    const m = this.mod!;

    if (this.loadedSystem === 'psx') {
      if (this.psxMemcardPath) {
        try {
          m.FS.writeFile(this.psxMemcardPath, data);
        } catch {
          return false;
        }
      }
      if (this.psxMcdPtr == null && this.pinnedSaveRam) {
        this.psxMcdPtr = this.findPsxMemcardPtr(this.pinnedSaveRam);
      }
      if (this.psxMcdPtr == null) {
        this.psxMcdPtr = this.findPsxMemcardPtr(data);
      }
      if (this.psxMcdPtr != null) {
        const n = Math.min(PSX_MEMCARD_BYTES, data.length);
        m.HEAPU8.set(data.subarray(0, n), this.psxMcdPtr);
        const pinned = new Uint8Array(PSX_MEMCARD_BYTES);
        pinned.set(data.subarray(0, n));
        this.pinnedSaveRam = pinned;
        return true;
      }
      // ポインタ未発見でも FS に書けていれば OK（次回 LoadMcd 相当は起動時のみ）
      if (this.psxMemcardPath) {
        this.pinnedSaveRam = data.slice(0, PSX_MEMCARD_BYTES);
        return true;
      }
      return false;
    }

    const size = this.retro.getMemorySize(RETRO_MEMORY_SAVE_RAM) as number;
    const ptr = this.retro.getMemoryData(RETRO_MEMORY_SAVE_RAM) as number;
    if (!size || !ptr) return false;
    const n = Math.min(size, data.length);
    m.HEAPU8.set(data.subarray(0, n), ptr);
    return true;
  }

  setVariable(key: string, value: string): void {
    this.variables[key] = value;
    this.variablesDirty = true;
  }

  dispose(): void {
    if (!this.mod) return;
    if (this.retroInited) {
      try {
        this.retro.unloadGame();
        this.retro.deinit();
      } catch (e) {
        console.warn('[libretro] dispose error', e);
      }
      this.retroInited = false;
    }
    for (const ptr of this.fnPtrs) {
      try {
        this.mod.removeFunction(ptr);
      } catch {
        /* テーブル解放は失敗しても致命的ではない */
      }
    }
    this.fnPtrs = [];
    this.strCache.clear();
    this.psxMemcardPath = null;
    this.psxMcdPtr = null;
    this.pinnedSaveRam = null;
    this.diskControl = null;
    this.mod = null;
  }
}

/**
 * 仮想 FS 用ファイル名。
 * CUE の FILE "Track 01.bin" と実ファイル名を一致させるため、
 * パス区切りと危険文字だけ落とし、空白や括弧は残す。
 */
function fsSafeName(name: string): string {
  const base = name.replace(/\\/g, '/').split('/').pop() || 'rom';
  if (base === '.' || base === '..') return 'rom';
  return base.replace(/[\0\r\n]/g, '_');
}

function byteLengthOf(data: Uint8Array | Blob): number {
  return data instanceof Blob ? data.size : data.byteLength;
}

async function toU8(data: Uint8Array | Blob): Promise<Uint8Array> {
  if (data instanceof Uint8Array) return data;
  return new Uint8Array(await data.arrayBuffer());
}
